import React, { useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import {
  TextField, Button, Grid, Typography, Chip, Box, Select, MenuItem,
  FormControl, InputLabel, CircularProgress, Collapse, IconButton
} from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import {
  ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
  Add as AddIcon, Close as CloseIcon
} from '@material-ui/icons'

const MATERIAL_CATEGORIES = [
  'concrete', 'dirt', 'rock', 'asphalt', 'tile',
  'green_waste', 'c_and_d', 'msw',
  'appliances', 'electronics', 'tires', 'scrap_metal',
  'special_waste', 'sludge', 'contaminated_soil'
]

const MATERIAL_LABELS = {
  concrete: 'Concrete', dirt: 'Dirt', rock: 'Rock', asphalt: 'Asphalt',
  tile: 'Tile', green_waste: 'Green Waste', c_and_d: 'C&D', msw: 'MSW',
  appliances: 'Appliances', electronics: 'Electronics', tires: 'Tires',
  scrap_metal: 'Scrap Metal', special_waste: 'Special Waste',
  sludge: 'Sludge', contaminated_soil: 'Contaminated Soil'
}

const MATERIAL_COLORS = {
  concrete: { bg: '#eef0f1', color: '#546e7a' },
  dirt: { bg: '#f0ebe6', color: '#6d4c41' },
  rock: { bg: '#eceff1', color: '#607d8b' },
  asphalt: { bg: '#e8ecef', color: '#455a64' },
  tile: { bg: '#f0f2f3', color: '#78909c' },
  green_waste: { bg: '#dceedf', color: '#2f7a3e' },
  c_and_d: { bg: '#fbe8d6', color: '#b35d1a' },
  msw: { bg: '#f7dccf', color: '#a9341d' },
  appliances: { bg: '#ece2fa', color: '#6e3fb7' },
  electronics: { bg: '#e6ebfa', color: '#3454c1' },
  tires: { bg: '#e8e8e8', color: '#424242' },
  scrap_metal: { bg: '#ececec', color: '#757575' },
  special_waste: { bg: '#fce4ec', color: '#c62828' },
  sludge: { bg: '#efebe9', color: '#5d4037' },
  contaminated_soil: { bg: '#fbe9e7', color: '#bf360c' }
}

const SCENARIO_LABELS = { OUR_TRUCK_20T: '20T Truck', OUR_TRAILER_10T: '10T Trailer' }
const MAX_SEARCH_MILES = 500
const COMBINED_PLANS_TO_SHOW = 3

function getJobsiteCoords (j) {
  const c = j?.location?.coordinates
  if (Array.isArray(c) && c.length === 2 && c[0] != null) return { lng: Number(c[0]), lat: Number(c[1]) }
  if (j?.latitude != null) return { lat: Number(j.latitude), lng: Number(j.longitude) }
  return null
}

function formatJobsiteLabel (j) {
  if (!j) return ''
  const name = j.name || j.address || 'Unnamed'
  const client = j.client?.companyName || ''
  const addr = [j.address, j.city, j.state, j.zip].filter(Boolean).join(', ')
  return [name, client, addr].filter(Boolean).join(' — ')
}

function formatMoney (n) {
  if (n == null) return '—'
  return `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

const matChipStyle = (cat) => {
  const mc = MATERIAL_COLORS[cat] || { bg: '#f0f0f0', color: '#666' }
  return { backgroundColor: mc.bg, color: mc.color, fontWeight: 600, border: `1px solid ${mc.color}22`, fontSize: 10 }
}

/**
 * Build combined plans: find facilities that accept ALL requested materials,
 * pick the cheapest scenario per material at each facility, sum total cost.
 */
function buildCombinedPlans (resultGroups) {
  if (!resultGroups || resultGroups.length <= 1) return null

  // Collect all facility IDs that appear in every material group
  const facilityByGroup = resultGroups.map(g => {
    const map = new Map()
    for (const opt of g.options) {
      const key = `${opt.facilityId}_${opt.scenario}`
      if (!map.has(key) || opt.totalCost < map.get(key).totalCost) {
        map.set(key, opt)
      }
    }
    return map
  })

  // Find facility+scenario combos that exist in ALL groups
  const firstGroup = facilityByGroup[0]
  const plans = []

  for (const [key, firstOpt] of firstGroup) {
    const allPresent = facilityByGroup.every(m => m.has(key))
    if (!allPresent) continue

    const materialBreakdown = resultGroups.map((g, i) => {
      const opt = facilityByGroup[i].get(key)
      return { material: g.material, quantity: g.quantity, ...opt }
    })

    const totalCost = materialBreakdown.reduce((sum, o) => sum + (o.totalCost || 0), 0)
    const totalDumpFees = materialBreakdown.reduce((sum, o) => sum + (o.dumpFees || 0), 0)
    const totalTransport = materialBreakdown.reduce((sum, o) => sum + (o.transportCost || 0), 0)
    const totalTrips = materialBreakdown.reduce((sum, o) => sum + (o.trips || 0), 0)

    plans.push({
      facilityId: firstOpt.facilityId,
      facilityName: firstOpt.facilityName,
      facilityType: firstOpt.facilityType,
      facilityAddress: firstOpt.facilityAddress,
      facilityDistanceMiles: firstOpt.facilityDistanceMiles,
      scenario: firstOpt.scenario,
      totalCost: Math.round(totalCost * 100) / 100,
      totalDumpFees: Math.round(totalDumpFees * 100) / 100,
      totalTransport: Math.round(totalTransport * 100) / 100,
      totalTrips,
      materialBreakdown
    })
  }

  plans.sort((a, b) => a.totalCost - b.totalCost)
  return plans.slice(0, COMBINED_PLANS_TO_SHOW)
}

const DisposalQuickCalculator = ({ url }) => {
  const [jobsiteInput, setJobsiteInput] = useState('')
  const [jobsiteOptions, setJobsiteOptions] = useState([])
  const [jobsiteLoading, setJobsiteLoading] = useState(false)
  const [selectedJobsite, setSelectedJobsite] = useState(null)
  const jobsiteDebounceRef = useRef(null)

  const [materialRows, setMaterialRows] = useState([{ material: 'concrete', quantity: '' }])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [resultGroups, setResultGroups] = useState(null)
  const [expandedKey, setExpandedKey] = useState(null)
  const [showPerMaterial, setShowPerMaterial] = useState(false)

  useEffect(() => {
    if (jobsiteDebounceRef.current) clearTimeout(jobsiteDebounceRef.current)
    if (!jobsiteInput || jobsiteInput.length < 2) { setJobsiteOptions([]); return }
    setJobsiteLoading(true)
    jobsiteDebounceRef.current = setTimeout(() => {
      axios.get(`${url}/api/customer/jobsites/search`, { params: { q: jobsiteInput } })
        .then(res => {
          const data = Array.isArray(res.data?.jobsites) ? res.data.jobsites : []
          setJobsiteOptions(data.filter(s => Boolean(getJobsiteCoords(s))))
        })
        .catch(err => console.error('[DisposalCalc] jobsite search:', err))
        .finally(() => setJobsiteLoading(false))
    }, 300)
    return () => { if (jobsiteDebounceRef.current) clearTimeout(jobsiteDebounceRef.current) }
  }, [jobsiteInput, url])

  const updateRow = (idx, field, value) => setMaterialRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r))
  const addRow = () => {
    const used = new Set(materialRows.map(r => r.material))
    const next = MATERIAL_CATEGORIES.find(c => !used.has(c)) || 'dirt'
    setMaterialRows(prev => [...prev, { material: next, quantity: '' }])
  }
  const removeRow = (idx) => { if (materialRows.length > 1) setMaterialRows(prev => prev.filter((_, i) => i !== idx)) }

  const validRows = materialRows.filter(r => r.material && r.quantity && Number(r.quantity) > 0)
  const canCalculate = selectedJobsite && validRows.length > 0

  const handleCalculate = async () => {
    if (!canCalculate) return
    const coords = getJobsiteCoords(selectedJobsite)
    if (!coords) { setError('Selected jobsite has no coordinates'); return }
    setLoading(true); setError(''); setResultGroups(null); setExpandedKey(null); setShowPerMaterial(false)
    try {
      const promises = validRows.map(row =>
        axios.post(`${url}/api/aggregate-dashboard/fulfillment-calc/disposal`, {
          materialCategory: row.material, quantity: Number(row.quantity),
          jobsiteLat: coords.lat, jobsiteLng: coords.lng, maxDistMiles: MAX_SEARCH_MILES
        }).then(resp => ({
          material: row.material, quantity: row.quantity,
          options: resp.data.options || [], cheapest: resp.data.cheapest
        })).catch(err => ({
          material: row.material, quantity: row.quantity, options: [], cheapest: null,
          error: err.response?.data?.error || err.message
        }))
      )
      setResultGroups(await Promise.all(promises))
    } catch (err) { setError(err.response?.data?.error || err.message) }
    finally { setLoading(false) }
  }

  const combinedPlans = useMemo(() => buildCombinedPlans(resultGroups), [resultGroups])
  const isMultiMaterial = resultGroups && resultGroups.length > 1
  const totalTons = validRows.reduce((s, r) => s + Number(r.quantity || 0), 0)

  return (
    <div style={{ padding: '0 8px' }}>
      {/* ── Input Section ─────────────────────────── */}
      <div style={{
        background: 'var(--surface, #fbf8f1)', borderRadius: 12, padding: '16px 20px',
        border: '1px solid var(--line, #e0d8c2)', marginBottom: 16
      }}>
        <Grid container spacing={2} alignItems="flex-end" style={{ marginBottom: 12 }}>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              value={selectedJobsite} onChange={(e, val) => setSelectedJobsite(val)}
              inputValue={jobsiteInput} onInputChange={(e, val) => setJobsiteInput(val)}
              options={jobsiteOptions} loading={jobsiteLoading}
              getOptionLabel={formatJobsiteLabel}
              renderInput={params => (
                <TextField {...params} label="Jobsite" variant="outlined" size="small"
                  InputProps={{ ...params.InputProps, style: { fontSize: 13 },
                    endAdornment: (<>{jobsiteLoading ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</>)
                  }}
                />
              )}
              noOptionsText={jobsiteInput.length < 2 ? 'Type to search...' : 'No jobsites with coordinates'}
            />
          </Grid>
          <Grid item style={{ flex: 1 }} />
          <Grid item>
            <Button variant="contained" disabled={!canCalculate || loading} onClick={handleCalculate}
              startIcon={loading ? <CircularProgress size={14} style={{ color: '#fbf8f1' }} /> : null}
              style={{
                background: canCalculate && !loading ? 'var(--primary, #1f5e3a)' : 'var(--ink-4, #97a094)',
                color: '#fbf8f1', textTransform: 'none', borderRadius: 10, fontWeight: 600,
                height: 40, minWidth: 140
              }}>
              {loading ? 'Calculating...' : 'Calculate'}
            </Button>
          </Grid>
        </Grid>

        <div style={{ background: 'var(--surface-2, #f6f1e3)', borderRadius: 8, padding: '10px 12px', border: '1px solid var(--line, #e0d8c2)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'var(--ink-3, #6f7a6b)', fontFamily: 'var(--font-mono)' }}>
              Materials to Haul
            </span>
            <Button size="small" startIcon={<AddIcon style={{ fontSize: 14 }} />} onClick={addRow}
              style={{ fontSize: 11, textTransform: 'none', color: 'var(--primary, #1f5e3a)', fontWeight: 600, padding: '2px 8px' }}>
              Add Material
            </Button>
          </div>
          {materialRows.map((row, idx) => (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: idx < materialRows.length - 1 ? 6 : 0 }}>
              <FormControl variant="outlined" size="small" style={{ minWidth: 160, flex: 1 }}>
                <Select value={row.material} onChange={e => updateRow(idx, 'material', e.target.value)}
                  style={{ fontSize: 13, background: 'var(--surface, #fbf8f1)' }}>
                  {MATERIAL_CATEGORIES.map(cat => <MenuItem key={cat} value={cat}>{MATERIAL_LABELS[cat]}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField type="number" size="small" variant="outlined" placeholder="Tons"
                value={row.quantity} onChange={e => updateRow(idx, 'quantity', e.target.value)}
                inputProps={{ min: 1, step: 1 }}
                InputProps={{ style: { fontSize: 13, background: 'var(--surface, #fbf8f1)' } }}
                style={{ width: 100 }} />
              <span style={{ fontSize: 11, color: 'var(--ink-4, #97a094)' }}>tons</span>
              {materialRows.length > 1 && (
                <IconButton size="small" onClick={() => removeRow(idx)} style={{ padding: 4 }}>
                  <CloseIcon style={{ fontSize: 16, color: 'var(--ink-4, #97a094)' }} />
                </IconButton>
              )}
            </div>
          ))}
        </div>
      </div>

      {error && (
        <Box mb={2} p={1.5} style={{ background: 'var(--disposal-soft, #f7dccf)', borderRadius: 10, border: '1px solid rgba(169,52,29,.2)' }}>
          <Typography variant="body2" style={{ color: 'var(--disposal, #a9341d)', fontSize: 12 }}>{error}</Typography>
        </Box>
      )}

      {!resultGroups && !loading && !error && (
        <Box py={6} textAlign="center">
          <Typography variant="body2" style={{ color: 'var(--ink-4, #97a094)', fontSize: 13 }}>
            Select a jobsite, add materials with quantities, then calculate haul-off options
          </Typography>
        </Box>
      )}

      {/* ── Combined Plans (multi-material) ────────── */}
      {isMultiMaterial && combinedPlans && combinedPlans.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--ink-3, #6f7a6b)', fontFamily: 'var(--font-mono)' }}>
              BEST OPTIONS — ALL MATERIALS
            </span>
            <span style={{ fontSize: 11, color: 'var(--ink-4, #97a094)' }}>
              {totalTons} tons total &middot; facilities that accept everything
            </span>
          </div>

          {combinedPlans.map((plan, pIdx) => {
            const key = `combined-${pIdx}`
            const isExpanded = expandedKey === key
            const isBest = pIdx === 0

            return (
              <div key={key} style={{
                background: isBest ? 'var(--primary-soft, #e3eee0)' : 'var(--surface, #fbf8f1)',
                border: `1px solid ${isBest ? 'rgba(31,94,58,.25)' : 'var(--line, #e0d8c2)'}`,
                borderRadius: 10, marginBottom: 8, overflow: 'hidden', cursor: 'pointer',
                boxShadow: isBest ? '0 1px 4px rgba(31,94,58,.12)' : 'none'
              }} onClick={() => setExpandedKey(isExpanded ? null : key)}>
                <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: isBest ? 'var(--primary, #1f5e3a)' : 'var(--surface-2, #f6f1e3)',
                    color: isBest ? '#fbf8f1' : 'var(--ink-3, #6f7a6b)', fontSize: 12, fontWeight: 700, flexShrink: 0
                  }}>#{pIdx + 1}</div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--ink, #1c2419)' }}>{plan.facilityName}</span>
                      <Chip size="small" label={SCENARIO_LABELS[plan.scenario] || plan.scenario}
                        variant="outlined" style={{ fontSize: 9, height: 18, fontWeight: 600, color: 'var(--ink-3, #6f7a6b)' }} />
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--ink-4, #97a094)', marginTop: 3, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                      <span>{plan.facilityDistanceMiles} mi away</span>
                      <span>&middot;</span>
                      <span>{plan.totalTrips} total trips</span>
                      <span>&middot;</span>
                      {plan.materialBreakdown.map((mb, i) => (
                        <Chip key={i} size="small" label={`${MATERIAL_LABELS[mb.material]} ${mb.quantity}T`}
                          style={{ ...matChipStyle(mb.material), height: 16, fontSize: 9 }} />
                      ))}
                    </div>
                  </div>

                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{
                      fontSize: 20, fontWeight: 800, color: isBest ? 'var(--primary, #1f5e3a)' : 'var(--ink, #1c2419)',
                      fontFamily: 'var(--font-mono)', letterSpacing: -0.5
                    }}>{formatMoney(plan.totalCost)}</div>
                    <div style={{ fontSize: 10, color: 'var(--ink-4, #97a094)', marginTop: 1 }}>
                      {formatMoney(plan.totalDumpFees)} dump &middot; {formatMoney(plan.totalTransport)} transport
                    </div>
                  </div>

                  <div style={{ flexShrink: 0, color: 'var(--ink-4, #97a094)' }}>
                    {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  </div>
                </div>

                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <div style={{ padding: '0 16px 14px', borderTop: '1px solid var(--line, #e0d8c2)', paddingTop: 12 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--ink-3, #6f7a6b)', letterSpacing: '0.08em', fontFamily: 'var(--font-mono)' }}>
                      Per-Material Breakdown
                    </span>
                    {plan.materialBreakdown.map((mb, i) => (
                      <div key={i} style={{
                        display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0',
                        borderBottom: i < plan.materialBreakdown.length - 1 ? '1px solid var(--line, #e0d8c2)' : 'none'
                      }}>
                        <Chip size="small" label={MATERIAL_LABELS[mb.material]} style={matChipStyle(mb.material)} />
                        <span style={{ fontSize: 12, color: 'var(--ink-2, #3d463a)' }}>
                          {mb.quantity} tons &middot; {mb.trips} trip{mb.trips !== 1 ? 's' : ''}
                        </span>
                        <span style={{ flex: 1 }} />
                        <span style={{ fontSize: 12, color: 'var(--ink-2, #3d463a)' }}>
                          {formatMoney(mb.dumpFees)} dump
                        </span>
                        <span style={{ fontSize: 12, color: 'var(--ink-2, #3d463a)' }}>
                          {formatMoney(mb.transportCost)} transport
                        </span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink, #1c2419)', fontFamily: 'var(--font-mono)' }}>
                          {formatMoney(mb.totalCost)}
                        </span>
                      </div>
                    ))}
                    {plan.facilityAddress && (
                      <div style={{ fontSize: 11, color: 'var(--ink-4, #97a094)', marginTop: 8 }}>
                        {plan.facilityAddress}
                      </div>
                    )}
                  </div>
                </Collapse>
              </div>
            )
          })}

          {combinedPlans.length === 0 && (
            <Box py={2} px={2} style={{ background: 'var(--surface-2, #f6f1e3)', borderRadius: 8, border: '1px solid var(--line, #e0d8c2)' }}>
              <Typography variant="body2" style={{ color: 'var(--ink-4, #97a094)', fontSize: 12 }}>
                No single facility accepts all your materials. See per-material options below.
              </Typography>
            </Box>
          )}
        </div>
      )}

      {/* ── Single-material results OR per-material detail ── */}
      {resultGroups && (
        <div>
          {isMultiMaterial && (
            <Button size="small" onClick={() => setShowPerMaterial(!showPerMaterial)}
              style={{ fontSize: 11, textTransform: 'none', color: 'var(--ink-3, #6f7a6b)', fontWeight: 600, marginBottom: 8 }}>
              {showPerMaterial ? 'Hide' : 'Show'} per-material breakdown ({resultGroups.reduce((s, g) => s + g.options.length, 0)} options)
            </Button>
          )}

          {(!isMultiMaterial || showPerMaterial) && resultGroups.map((group, gIdx) => (
            <div key={gIdx} style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <Chip size="small" label={MATERIAL_LABELS[group.material]} style={matChipStyle(group.material)} />
                <span style={{ fontSize: 11, color: 'var(--ink-3, #6f7a6b)', fontFamily: 'var(--font-mono)', fontWeight: 600 }}>
                  {group.quantity} TONS
                </span>
                <span style={{ fontSize: 11, color: 'var(--ink-4, #97a094)' }}>
                  &middot; {group.options.length} option{group.options.length !== 1 ? 's' : ''}
                </span>
              </div>

              {group.options.length === 0 && !group.error && (
                <Box py={2} px={2} style={{ background: 'var(--surface-2, #f6f1e3)', borderRadius: 8, border: '1px solid var(--line, #e0d8c2)' }}>
                  <Typography variant="body2" style={{ color: 'var(--ink-4, #97a094)', fontSize: 12 }}>
                    No facilities found that accept {MATERIAL_LABELS[group.material]}
                  </Typography>
                </Box>
              )}

              {group.options.slice(0, isMultiMaterial ? 5 : undefined).map((opt, oIdx) => {
                const key = `${gIdx}-${oIdx}`
                const isExpanded = expandedKey === key
                const isCheapest = oIdx === 0
                return (
                  <div key={key} style={{
                    background: isCheapest ? 'var(--primary-soft, #e3eee0)' : 'var(--surface, #fbf8f1)',
                    border: `1px solid ${isCheapest ? 'rgba(31,94,58,.25)' : 'var(--line, #e0d8c2)'}`,
                    borderRadius: 10, marginBottom: 6, overflow: 'hidden', cursor: 'pointer',
                    boxShadow: isCheapest ? '0 1px 4px rgba(31,94,58,.12)' : 'none'
                  }} onClick={() => setExpandedKey(isExpanded ? null : key)}>
                    <div style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{
                        width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: isCheapest ? 'var(--primary, #1f5e3a)' : 'var(--surface-2, #f6f1e3)',
                        color: isCheapest ? '#fbf8f1' : 'var(--ink-3, #6f7a6b)', fontSize: 10, fontWeight: 700, flexShrink: 0
                      }}>#{opt.rank}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontWeight: 700, fontSize: 12, color: 'var(--ink, #1c2419)' }}>{opt.facilityName}</span>
                          <Chip size="small" label={SCENARIO_LABELS[opt.scenario] || opt.scenario}
                            variant="outlined" style={{ fontSize: 9, height: 16, fontWeight: 600, color: 'var(--ink-3, #6f7a6b)' }} />
                        </div>
                        <div style={{ fontSize: 10, color: 'var(--ink-4, #97a094)', marginTop: 1 }}>
                          {opt.facilityDistanceMiles} mi &middot; {opt.trips} trip{opt.trips !== 1 ? 's' : ''}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontSize: 15, fontWeight: 800, color: isCheapest ? 'var(--primary, #1f5e3a)' : 'var(--ink, #1c2419)', fontFamily: 'var(--font-mono)' }}>
                          {formatMoney(opt.totalCost)}
                        </div>
                        <div style={{ fontSize: 9, color: 'var(--ink-4, #97a094)' }}>
                          {formatMoney(opt.dumpFees)} dump &middot; {formatMoney(opt.transportCost)} transport
                        </div>
                      </div>
                      <div style={{ flexShrink: 0, color: 'var(--ink-4, #97a094)' }}>
                        {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </div>
                    </div>
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <div style={{ padding: '0 16px 10px', borderTop: '1px solid var(--line, #e0d8c2)', paddingTop: 8 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 24px', fontSize: 11 }}>
                          <DetailRow label="Round-trip" value={`${opt.routeDistanceMiles} mi (${opt.routeDurationMinutes} min)`} />
                          <DetailRow label="Mileage" value={`${formatMoney(opt.mileageCost)} at $${opt.costPerMile}/mi`} />
                          <DetailRow label="Operator" value={`${formatMoney(opt.operatorCost)} at $${opt.operatorCostPerHour}/hr`} />
                          <DetailRow label="Dump fee"
                            value={opt.dumpFeeUnit === 'PER_TON'
                              ? `${formatMoney(opt.dumpFeeRate)}/ton x ${group.quantity}T`
                              : `${formatMoney(opt.dumpFeeRate)}/load x ${opt.trips}`} />
                          {opt.facilityAddress && <DetailRow label="Address" value={opt.facilityAddress} span />}
                        </div>
                      </div>
                    </Collapse>
                  </div>
                )
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const DetailRow = ({ label, value, span }) => (
  <div style={{ ...(span ? { gridColumn: '1 / -1' } : {}) }}>
    <span style={{ fontWeight: 600, color: 'var(--ink-3, #6f7a6b)', fontSize: 10, marginRight: 4 }}>{label}:</span>
    <span style={{ color: 'var(--ink-2, #3d463a)' }}>{value}</span>
  </div>
)

export default DisposalQuickCalculator
